import { valueAt, visiblePoints } from './scales';
import type {
    ChartSeries,
    SeriesCandle,
    SeriesMarker,
    SeriesPoint,
} from './seriesTypes';

export const MAX_MARKERS_PER_SERIES = 400;

export interface SeriesView {
    series: ChartSeries;
    points: SeriesPoint[];
    candles: SeriesCandle[];
    markers: SeriesMarker[];
}

export function thinMarkers(
    markers: readonly SeriesMarker[],
    max: number = MAX_MARKERS_PER_SERIES
): SeriesMarker[] {
    if (markers.length <= max) return [...markers];
    const kept = new Set(
        [...markers]
            .sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0))
            .slice(0, max)
            .map(m => m.id)
    );
    return markers.filter(m => kept.has(m.id));
}

export function seriesView(
    series: ChartSeries,
    domain: readonly [number, number],
    nowMs: number
): SeriesView {
    const [t0, t1] = domain;
    const view: SeriesView = { series, points: [], candles: [], markers: [] };
    if (series.data.kind === 'points') {
        const points = visiblePoints(series.data.points, domain);
        if (
            (series.draw === 'step' || series.draw === 'area') &&
            points.length > 0
        ) {
            const last = points[points.length - 1];
            const edge = Math.min(t1, nowMs);
            if (edge > last.t) points.push({ t: edge, v: last.v });
        }
        view.points = points;
    } else if (series.data.kind === 'candles') {
        view.candles = series.data.candles.filter(c => c.t >= t0 && c.t <= t1);
    } else {
        view.markers = thinMarkers(
            series.data.markers.filter(m => m.t >= t0 && m.t <= t1)
        );
    }
    return view;
}

export function axisValues(view: SeriesView): number[] {
    if (view.candles.length > 0) {
        return view.candles.flatMap(c => [c.low, c.high]);
    }
    return view.points.map(p => p.v);
}

export function candleAt(
    candles: readonly SeriesCandle[],
    t: number
): SeriesCandle | null {
    let found: SeriesCandle | null = null;
    for (const c of candles) {
        if (c.t > t) break;
        found = c;
    }
    return found;
}

export function readout(series: ChartSeries, t: number): number | null {
    if (series.data.kind === 'points') return valueAt(series.data.points, t);
    if (series.data.kind === 'candles') {
        return candleAt(series.data.candles, t)?.close ?? null;
    }
    return null;
}

/** True when the value at t is a streamed price: the series ends in a live point at or before t. */
export function isLiveReadout(series: ChartSeries, t: number): boolean {
    if (!series.live || series.data.kind !== 'points') return false;
    const last = series.data.points[series.data.points.length - 1];
    return last !== undefined && last.t <= t;
}

export function stepMs(times: readonly number[]): number {
    let step = Infinity;
    for (let i = 1; i < times.length && i < 200; i += 1) {
        const d = times[i] - times[i - 1];
        if (d > 0 && d < step) step = d;
    }
    return Number.isFinite(step) ? step : 60_000;
}

/**
 * The value a series has at the start of a window: its reading at t0, or its
 * first reading after t0 when it only starts later (a candle series: the
 * close at or before t0, else the open of the first candle after it).
 */
export function startValue(series: ChartSeries, t0: number): number | null {
    if (series.data.kind === 'points') {
        return (
            valueAt(series.data.points, t0) ??
            series.data.points.find(p => p.t >= t0)?.v ??
            null
        );
    }
    if (series.data.kind === 'candles') {
        return (
            candleAt(series.data.candles, t0)?.close ??
            series.data.candles.find(c => c.t >= t0)?.open ??
            null
        );
    }
    return null;
}

export interface ReadoutRow {
    series: ChartSeries;
    value: number;
}

/**
 * The series that have a reading at `time`, each with it. The floating
 * tooltip and the phone values strip both list this. One unit: highest
 * first. Mixed units keep the order of the series, as they always did,
 * except when clock values are among them (the clock on the main chart):
 * then each unit is sorted on its own and the units stay in the order they
 * appear, so probabilities stay sorted next to the clock.
 */
export function readoutRows(
    series: readonly ChartSeries[],
    time: number
): ReadoutRow[] {
    const rows: ReadoutRow[] = [];
    for (const s of series) {
        const value = readout(s, time);
        if (value !== null) rows.push({ series: s, value });
    }
    if (rows.every(r => r.series.unit === rows[0]?.series.unit)) {
        return rows.sort((a, b) => b.value - a.value);
    }
    if (!rows.some(r => r.series.unit === 'clock')) return rows;
    const byUnit = new Map<string, ReadoutRow[]>();
    for (const row of rows) {
        const group = byUnit.get(row.series.unit);
        if (group) group.push(row);
        else byUnit.set(row.series.unit, [row]);
    }
    return [...byUnit.values()].flatMap(group =>
        group.sort((a, b) => b.value - a.value)
    );
}
